/* eslint-disable react/forbid-dom-props */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calculator, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { petitionsApi } from '../services/api';

/**
 * Memória de cálculo dos pedidos, recalculada da ficha atual.
 *
 * Existe porque valor que ninguém consegue reproduzir é pior que nenhum valor: o advogado
 * assina a peça e precisa conferir de onde saiu cada número antes de protocolar. É a mesma conta
 * usada na geração — aqui ela só é exibida, sempre a partir da ficha de agora.
 */
export default function CalculoPanel({ caseId }) {
  const [aberto, setAberto] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['calculo-pedidos', caseId],
    queryFn: () => petitionsApi.getCalculo(caseId),
    enabled: !!caseId,
  });

  if (isLoading) return null;

  const pedidos = data?.pedidos || [];
  const pendencias = data?.pendencias || [];
  if (!pedidos.length && !pendencias.length) return null;

  const total = Number(data.total || 0).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
  });

  return (
    <>
      {pendencias.length > 0 && (
        <div className="card calculo-pendencias">
          <div className="card-header">
            <AlertTriangle size={15} />
            <span className="card-title">Pendências de cálculo</span>
            <span className="badge">{pendencias.length}</span>
          </div>
          <p>
            A geração não é bloqueada. Preencha estes campos para substituir pedidos sem valor.
          </p>
          <div className="calculo-pendencias-lista">
            {pendencias.map(p => (
              <div key={p.pedido_id} className="calculo-pendencia">
                <strong>{p.pedido}</strong>
                <span>Campo: {p.campo_label}</span>
                <Link to={`/ficha/${caseId}?tab=${p.aba}`}>
                  Corrigir na aba {p.aba === 'jornada' ? 'Jornada' : 'Contrato'}
                  <ExternalLink size={11} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
      {pedidos.length > 0 && <div className="card">
      <div className="card-header">
        <Calculator size={15} style={{ color: 'var(--gold)' }} />
        <span className="card-title">Memória de cálculo</span>
        <span className="badge" style={{ marginLeft: 'auto', fontWeight: 600 }}>{total}</span>
      </div>

      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: '.625rem' }}>
        Estimativa por aproximação (art. 840, §1º, da CLT e art. 12, §2º, da IN 41/2018 do TST).
        Clique num pedido para ver a conta.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
        {pedidos.map(p => {
          const expandido = aberto === p.pedido_id;
          return (
            <div key={p.pedido_id} style={{ borderTop: '1px solid var(--border-light)', paddingTop: '.375rem' }}>
              <button
                type="button"
                onClick={() => setAberto(expandido ? null : p.pedido_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '.375rem', width: '100%',
                  background: 'none', border: 'none', padding: '.125rem 0', cursor: 'pointer',
                  textAlign: 'left', fontSize: '.78rem', color: 'var(--text-primary)',
                }}
              >
                {expandido ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <span style={{ flex: 1 }}>{p.descricao}</span>
                <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{p.valor_formatado}</span>
                {p.origem === 'ficha' && (
                  <span className="badge" style={{ fontSize: '.62rem' }}>valor da ficha</span>
                )}
              </button>
              {expandido && (
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', padding: '.25rem 0 .5rem 1.25rem' }}>
                  {p.memoria.map((linha, i) => <div key={i} style={{ marginBottom: '.25rem' }}>{linha}</div>)}
                  {p.fundamento && (
                    <div style={{ marginTop: '.375rem', fontStyle: 'italic' }}>{p.fundamento}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>}
    </>
  );
}
